import { useEffect, useRef, useState } from "react";
import {
  Box, Stack, Typography, Button, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, ToggleButton, ToggleButtonGroup, LinearProgress,
} from "@mui/material";
import { BRAND } from "../../styles/theme";
import { LoginEvent, LoginEventType, LoginHistory, getLoginHistory } from "../../services/userAccess";
import { StatusChip } from "./chips";

type Kind = "ok" | "bad" | "info" | "warn" | "neutral";
const EVENT: Record<LoginEventType, { label: string; kind: Kind }> = {
  login_ok:           { label: "Login OK",                      kind: "ok" },
  login_failed:       { label: "Failed login",                  kind: "bad" },
  login_locked:       { label: "Login while locked",            kind: "bad" },
  code_sent:          { label: "Code requested",                kind: "info" },
  code_mail_failed:   { label: "Code email NOT sent",           kind: "warn" },
  code_wait:          { label: "Code already sent — wait",      kind: "neutral" },
  code_locked:        { label: "Code refused — locked",         kind: "bad" },
  code_rejected:      { label: "Code refused — session active", kind: "warn" },
  code_unknown_user:  { label: "Unknown user",                  kind: "neutral" },
  locked:             { label: "LOCKED — unlock email sent",    kind: "bad" },
  locked_mail_failed: { label: "LOCKED — unlock email NOT sent", kind: "bad" },
  unlock_link:        { label: "Unlock link opened",            kind: "ok" },
  unlocked_admin:     { label: "Unlocked from Web Admin",       kind: "ok" },
};
const REASON: Record<string, string> = { WrongPassword: "wrong code", UserLocked: "user locked", NonActiveUser: "user not active", UserDoesNotExist: "user does not exist" };
const DAYS = [1, 7, 30];
const POLL_MS = 4000;

const fmt = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "America/Argentina/Buenos_Aires",
  });
};

function detail(e: LoginEvent): string {
  if (e.tipo === "login_failed") return REASON[e.motivo ?? ""] ?? e.motivo ?? "";
  if (e.tipo === "unlocked_admin") return `by ${e.by ?? "?"}${e.intentos != null ? ` · ${e.intentos} attempts` : ""}`;
  return "";
}

/**
 * Historial de logins de un usuario web, leído de los logs de CoreDB (no hay
 * tabla de intentos). La búsqueda es async: arranca la query y reconsulta.
 */
export default function LoginHistoryDialog({ email, onClose }: { email: string | null; onClose: () => void }) {
  const [dias, setDias] = useState(7);
  const [data, setData] = useState<LoginHistory | null>(null);
  const [err, setErr]   = useState<string | null>(null);
  const run = useRef(0);   // invalida polls de búsquedas anteriores / diálogo cerrado

  useEffect(() => {
    if (!email) { run.current++; return; }
    const id = ++run.current;
    setData(null); setErr(null);
    (async () => {
      try {
        let r = await getLoginHistory(email, { dias });
        while (r.estado === "corriendo" && run.current === id) {
          setData(r);
          await new Promise((ok) => setTimeout(ok, POLL_MS));
          if (run.current !== id) return;
          r = await getLoginHistory(email, { qid: r.query_id });
        }
        if (run.current !== id) return;
        if (r.estado === "error") setErr(r.error ?? "Search failed");
        setData(r);
      } catch (e: any) { if (run.current === id) setErr(String(e?.message ?? e)); }
    })();
  }, [email, dias]);

  const loading = !err && (!data || data.estado === "corriendo");
  const events = data?.eventos ?? [];
  const gb = (data?.stats?.bytesScanned ?? 0) / 1e9;

  return (
    <Dialog open={!!email} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Login history<Typography variant="body2" color="text.secondary">{email}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <ToggleButtonGroup size="small" exclusive value={dias} onChange={(_, v) => v && setDias(v)} disabled={loading}>
            {DAYS.map((d) => <ToggleButton key={d} value={d} sx={{ px: 1.5 }}>{d === 1 ? "24 h" : `${d} days`}</ToggleButton>)}
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          {data?.estado === "listo" && (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end">
              {Object.entries(data.resumen ?? {}).map(([k, n]) => (
                <StatusChip key={k} kind={EVENT[k as LoginEventType]?.kind ?? "neutral"} label={`${EVENT[k as LoginEventType]?.label ?? k}: ${n}`} />
              ))}
            </Stack>
          )}
        </Stack>
        {loading && <Box sx={{ my: 2 }}><LinearProgress /><Typography variant="caption" color="text.secondary">Searching CoreDB logs… (a 30-day search can take a minute)</Typography></Box>}
        {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
        {data?.truncado && <Alert severity="warning" sx={{ mb: 1 }}>Too many log lines: only the most recent are shown. Narrow the range.</Alert>}
        {data?.estado === "listo" && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["When (AR)", "Event", "Details", "IP"].map((h) => <TableCell key={h} sx={{ fontWeight: 700, bgcolor: BRAND.primaryLight }}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>No login activity in this range.</TableCell></TableRow>
                ) : events.map((e, i) => (
                  <TableRow key={`${e.t}-${e.tipo}-${i}`} hover>
                    <TableCell sx={{ whiteSpace: "nowrap", fontFamily: "monospace", fontSize: 12 }}>{fmt(e.t)}</TableCell>
                    <TableCell><StatusChip kind={EVENT[e.tipo]?.kind ?? "neutral"} label={EVENT[e.tipo]?.label ?? e.tipo} /></TableCell>
                    <TableCell>{detail(e)}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{e.ip ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Source: CoreDB API logs (CloudWatch, 180-day retention){gb > 0 ? ` · ${gb.toFixed(1)} GB scanned` : ""}.
          "Unlock link opened" means the link from the lock email was used.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{loading ? <><CircularProgress size={14} sx={{ mr: 1 }} />Close</> : "Close"}</Button>
      </DialogActions>
    </Dialog>
  );
}
